/* eslint complexity: 0 */
'use client'
import { Button, Checkbox, Input, LuneTheme, useDevice } from '@lune-fe/lune-ui-lib'
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined'
import MarkEmailReadOutlined from '@mui/icons-material/MarkEmailReadOutlined'
import type { SxProps } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Text from '@mui/material/Typography'
import { FC, useEffect, useState } from 'react'
import { useDebounce } from 'use-debounce'

import TwoColumnsWithBackgroundImage from '@/components/TwoColumnsWithBackgroundImage'
import { useNewsletter } from '@/hooks/useNewsLetter'

const spacing = {
    xs: 3,
    sm: 3,
    md: 6,
    lg: 8,
    xl: 10,
}

const ICONS = new Map([
    ['FileDownloadOutlined', FileDownloadOutlined],
    ['MarkEmailReadOutlined', MarkEmailReadOutlined],
])

export const LandingPageForm: FC<{
    hubspotFormId: string
    backgroundColor: string
    title: string
    description: string
    image: {
        url: string
        alt: string
    }
    buttonText: string
    buttonIconName: string
    confirmationText: string
    requestMarketingConsent: boolean
    sx?: SxProps
}> = ({
    hubspotFormId,
    title,
    description,
    image,
    buttonText,
    buttonIconName,
    confirmationText,
    requestMarketingConsent,
    backgroundColor,
    sx,
}) => {
    const { palette } = LuneTheme
    const { xsScreen, mdScreen } = useDevice()
    const { isValidEmail, toHubspot } = useNewsletter(hubspotFormId, [
        title,
        ...(description ? [description] : []),
        ...(requestMarketingConsent
            ? ['I am happy to receive marketing communications from Lune']
            : []),
    ])

    const [name, setName] = useState<string>('')
    const [email, setEmail] = useState<string>('')
    const [company, setCompany] = useState<string>('')
    const [nameError, setNameError] = useState<boolean>(false)
    const [emailError, setEmailError] = useState<boolean>(false)
    const [companyError, setCompanyError] = useState<boolean>(false)
    const [marketingConsent, setMarketingConsent] = useState<boolean>(false)
    const [success, setSuccess] = useState<boolean>()
    const [debouncedEmail] = useDebounce(email, 1000)

    useEffect(() => {
        if (isValidEmail(email) || email === '') {
            setEmailError(false)
        } else {
            setEmailError(true)
        }
    }, [debouncedEmail, email, isValidEmail])

    useEffect(() => {
        setNameError(false)
    }, [name])

    const onSubmit = async () => {
        const validName = name.length
        const validEmail = isValidEmail(email)
        const validCompany = company.length

        if (!validName || !validEmail || !validCompany) {
            !validName && setNameError(true)
            !validEmail && setEmailError(true)
            !validCompany && setCompanyError(true)
            return
        }

        const data = await toHubspot({
            name,
            email,
            company,
            legalConsent: marketingConsent ? 'marketing' : 'gatedContent',
        })
        if (data.status === 'error') {
            setSuccess(false)
        } else {
            setSuccess(true)
            setName('')
            setEmail('')
            setCompany('')
            setMarketingConsent(false)
        }
    }

    let Icon = ICONS.get(buttonIconName)
    if (Icon === undefined) {
        console.log(`Unrecognized icon ${buttonIconName}`)
        Icon = FileDownloadOutlined
    }

    return (
        <Stack
            direction={xsScreen ? 'column' : 'row'}
            display="flex"
            sx={{
                borderRadius: '32px',
                backgroundColor,
                overflow: 'hidden',
                ...(success && { display: 'flex', alignItems: 'center' }),
                ...(sx ?? {}),
            }}
        >
            <Stack
                spacing={spacing}
                sx={{
                    flex: 1,
                    padding: spacing,
                    height: '100%',
                    ...(xsScreen && { paddingTop: '40px !important' }),
                }}
            >
                {success ? (
                    <Box
                        sx={{
                            height: mdScreen ? '508px' : '572px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: mdScreen ? 4 : 9,
                            alignItems: 'flex-start',
                            justifyContent: 'center',
                        }}
                    >
                        <Text variant={mdScreen ? 'h5' : 'h4'}>{confirmationText}</Text>
                    </Box>
                ) : (
                    <Stack spacing={4}>
                        <Text variant={mdScreen ? 'h5' : 'h4'}>{title}</Text>
                        <Text variant={mdScreen ? 'body2' : 'body1'}>{description}</Text>
                        <Stack spacing={1}>
                            <Input
                                placeholder={'Name'}
                                error={nameError}
                                sx={{ backgroundColor: palette.White }}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                            <Input
                                placeholder={'Company Name'}
                                error={companyError}
                                sx={{ backgroundColor: palette.White }}
                                value={company}
                                onChange={(e) => setCompany(e.target.value)}
                            />
                            <Input
                                placeholder={'Email'}
                                error={emailError}
                                sx={{ backgroundColor: palette.White }}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                            {requestMarketingConsent && (
                                <Box pt={4}>
                                    <Checkbox
                                        sx={{ marginRight: '12px' }}
                                        checked={marketingConsent}
                                        onChange={(e) => setMarketingConsent(e.target.checked)}
                                    />

                                    <Text
                                        variant="body3"
                                        onClick={() => setMarketingConsent(!marketingConsent)}
                                        sx={{ '&:hover': { cursor: 'pointer' } }}
                                    >
                                        I am happy to receive marketing communications from Lune
                                    </Text>
                                </Box>
                            )}
                        </Stack>

                        <Button onClick={onSubmit} leftIcon={<Icon />} sx={{ width: '100%' }}>
                            {buttonText}
                        </Button>
                    </Stack>
                )}
            </Stack>
            <Box
                sx={{
                    flex: 1,
                    marginTop: spacing,
                    marginRight: spacing,
                    ...(xsScreen && { marginLeft: '24px' }),
                }}
            >
                <img
                    src={image.url}
                    alt={image.alt}
                    style={{
                        objectFit: 'contain',
                        maxWidth: '100%',
                        maxHeight: '100%',
                    }}
                />
            </Box>
        </Stack>
    )
}

export const LandingPageFullBackgroundForm: FC<{
    hubspotFormId: string
    title: string
    description: string
    image: {
        url: string
        alt: string
    }
    mobileImage: {
        url: string
        alt: string
    } | null
    buttonText: string
    buttonIconName: string
    confirmationText: string
    requestMarketingConsent: boolean
}> = ({
    hubspotFormId,
    title,
    description,
    image,
    mobileImage,
    buttonText,
    buttonIconName,
    confirmationText,
    requestMarketingConsent,
}) => {
    const { palette } = LuneTheme
    const { smScreen, xsScreen, mdScreen } = useDevice()
    const { isValidEmail, toHubspot } = useNewsletter(hubspotFormId, [
        title,
        ...(description ? [description] : []),
        ...(requestMarketingConsent
            ? ['I am happy to receive marketing communications from Lune']
            : []),
    ])

    const [name, setName] = useState<string>('')
    const [email, setEmail] = useState<string>('')
    const [company, setCompany] = useState<string>('')
    const [nameError, setNameError] = useState<boolean>(false)
    const [emailError, setEmailError] = useState<boolean>(false)
    const [companyError, setCompanyError] = useState<boolean>(false)
    const [marketingConsent, setMarketingConsent] = useState<boolean>(false)
    const [success, setSuccess] = useState<boolean>()
    const [debouncedEmail] = useDebounce(email, 1000)

    useEffect(() => {
        if (isValidEmail(email) || email === '') {
            setEmailError(false)
        } else {
            setEmailError(true)
        }
    }, [debouncedEmail, email, isValidEmail])

    useEffect(() => {
        setNameError(false)
    }, [name])

    const onSubmit = async () => {
        const validName = name.length
        const validEmail = isValidEmail(email)
        const validCompany = company.length

        if (!validName || !validEmail || !validCompany) {
            !validName && setNameError(true)
            !validEmail && setEmailError(true)
            !validCompany && setCompanyError(true)
            return
        }

        const data = await toHubspot({
            name,
            email,
            company,
            legalConsent: marketingConsent ? 'marketing' : 'gatedContent',
        })
        if (data.status === 'error') {
            setSuccess(false)
        } else {
            setSuccess(true)
            setName('')
            setEmail('')
            setCompany('')
            setMarketingConsent(false)
        }
    }

    let Icon = ICONS.get(buttonIconName)
    if (Icon === undefined) {
        console.log(`Unrecognized icon ${buttonIconName}`)
        Icon = FileDownloadOutlined
    }

    return (
        <TwoColumnsWithBackgroundImage
            backgroundImage={image.url}
            mobileBackgroundImage={mobileImage ? mobileImage.url : image.url}
            rightSide={<Box sx={{ height: smScreen || xsScreen ? '350px' : 'auto' }} />}
            leftSide={
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        flexDirection: 'column',
                        gap: mdScreen ? 3 : 4,
                    }}
                >
                    {success ? (
                        <Box
                            sx={{
                                height: mdScreen ? '508px' : '572px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: mdScreen ? 4 : 9,
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                            }}
                        >
                            <Text variant={mdScreen ? 'h5' : 'h4'}>{confirmationText}</Text>
                        </Box>
                    ) : (
                        <Stack spacing={4}>
                            <Text variant={mdScreen ? 'h5' : 'h4'}>{title}</Text>
                            <Text variant={mdScreen ? 'body2' : 'body1'}>{description}</Text>
                            <Stack spacing={1}>
                                <Input
                                    placeholder={'Name'}
                                    error={nameError}
                                    sx={{ backgroundColor: palette.White }}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                />
                                <Input
                                    placeholder={'Company Name'}
                                    error={companyError}
                                    sx={{ backgroundColor: palette.White }}
                                    value={company}
                                    onChange={(e) => setCompany(e.target.value)}
                                />
                                <Input
                                    placeholder={'Email'}
                                    error={emailError}
                                    sx={{ backgroundColor: palette.White }}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                                {requestMarketingConsent && (
                                    <Box pt={4}>
                                        <Checkbox
                                            sx={{ marginRight: '12px' }}
                                            checked={marketingConsent}
                                            onChange={(e) => setMarketingConsent(e.target.checked)}
                                        />

                                        <Text
                                            variant="body3"
                                            onClick={() => setMarketingConsent(!marketingConsent)}
                                            sx={{ '&:hover': { cursor: 'pointer' } }}
                                        >
                                            I am happy to receive marketing communications from Lune
                                        </Text>
                                    </Box>
                                )}
                            </Stack>

                            <Button onClick={onSubmit} leftIcon={<Icon />}>
                                {buttonText}
                            </Button>
                        </Stack>
                    )}
                </Box>
            }
        />
    )
}
